import { useState } from "react";
import { Loader2, MessageSquareText } from "lucide-react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  emptyShortLeadForm,
  getLeadFormErrorMessage,
  submitShortLeadForm,
  shortLeadFormSchema,
  type ShortLeadFormInput,
} from "@/lib/leadForms";

const countryCodes = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+33", label: "🇫🇷 +33" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+966", label: "🇸🇦 +966" },
  { code: "+974", label: "🇶🇦 +974" },
  { code: "+968", label: "🇴🇲 +968" },
  { code: "+973", label: "🇧🇭 +973" },
  { code: "+965", label: "🇰🇼 +965" },
  { code: "+27", label: "🇿🇦 +27" },
  { code: "+234", label: "🇳🇬 +234" },
  { code: "+254", label: "🇰🇪 +254" },
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+63", label: "🇵🇭 +63" },
  { code: "+82", label: "🇰🇷 +82" },
  { code: "+39", label: "🇮🇹 +39" },
  { code: "+34", label: "🇪🇸 +34" },
  { code: "+7", label: "🇷🇺 +7" },
  { code: "+55", label: "🇧🇷 +55" },
  { code: "+52", label: "🇲🇽 +52" },
  { code: "+92", label: "🇵🇰 +92" },
  { code: "+880", label: "🇧🇩 +880" },
  { code: "+94", label: "🇱🇰 +94" },
  { code: "+977", label: "🇳🇵 +977" },
];

const FloatingEnquireButton = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<ShortLeadFormInput>(emptyShortLeadForm);
  const [countryCode, setCountryCode] = useState("+91");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleChange = (field: keyof ShortLeadFormInput, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handlePhoneChange = (value: string) => {
    const digitsOnly = value.replace(/[^\d\s]/g, "");
    handleChange("phone", digitsOnly);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFieldErrors({});
    setIsSubmitting(true);

    const submissionForm: ShortLeadFormInput = {
      ...form,
      phone: form.phone?.trim() ? `${countryCode} ${form.phone.trim()}` : "",
    };

    const parsed = shortLeadFormSchema.safeParse(submissionForm);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      parsed.error.issues.forEach((issue) => {
        const field = String(issue.path[0] ?? "");
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      });
      setFieldErrors(errors);
      setIsSubmitting(false);
      return;
    }

    try {
      await submitShortLeadForm(submissionForm, "floating-enquire");
      toast({
        title: "Enquiry sent",
        description: "Thanks — we'll get back to you shortly.",
      });
      setForm(emptyShortLeadForm);
      setCountryCode("+91");
      setFieldErrors({});
      setOpen(false);
    } catch (error) {
      toast({
        title: "Unable to send enquiry",
        description: getLeadFormErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open enquiry form"
        className="group fixed bottom-6 right-6 z-50 h-16 w-16 overflow-hidden rounded-full border border-accent/45 bg-gradient-to-br from-primary via-primary to-secondary px-0 text-primary-foreground shadow-[0_24px_60px_-18px_hsl(var(--primary)/0.95)] transition-all duration-300 hover:-translate-y-1.5 hover:scale-[1.03] hover:border-accent/70 hover:shadow-[0_30px_80px_-18px_hsl(var(--primary)/1)] focus-visible:ring-accent md:h-14 md:w-auto md:px-5"
      >
        <span className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/10 via-transparent to-accent/30 opacity-90" aria-hidden="true" />
        <span className="absolute -inset-3 rounded-full bg-primary/35 blur-2xl transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true" />
        <span className="absolute inset-[2px] rounded-full bg-gradient-to-r from-primary/95 via-secondary to-primary/90" aria-hidden="true" />
        <span className="absolute right-2 top-2 h-3 w-3 rounded-full bg-accent shadow-[0_0_18px_hsl(var(--accent)/0.9)]" aria-hidden="true" />
        <span className="relative z-10 flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-[inset_0_1px_0_hsl(var(--background)/0.5),0_10px_25px_-12px_hsl(var(--accent)/0.9)] transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
            <MessageSquareText className="h-5 w-5" />
          </span>
          <span className="hidden text-sm font-semibold tracking-[0.08em] text-primary-foreground md:inline">Enquire Now</span>
        </span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden border border-accent/35 bg-transparent p-0 shadow-[0_35px_100px_-24px_hsl(var(--primary)/0.95)] sm:max-w-[560px]">
          <div className="relative rounded-[calc(var(--radius)+0.6rem)] border border-primary-foreground/10 bg-gradient-to-br from-primary via-primary to-secondary p-6 text-primary-foreground sm:p-7">
            <div className="absolute -left-10 top-0 h-36 w-36 rounded-full bg-accent/30 blur-3xl" aria-hidden="true" />
            <div className="absolute -right-8 bottom-0 h-40 w-40 rounded-full bg-primary-foreground/10 blur-3xl" aria-hidden="true" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(var(--accent)/0.22),transparent_30%),radial-gradient(circle_at_bottom_left,hsl(var(--background)/0.08),transparent_28%)]" aria-hidden="true" />

            <div className="relative">
              <DialogHeader className="space-y-3 text-left">
                <div className="inline-flex w-fit items-center rounded-full border border-accent/45 bg-accent/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-accent-foreground shadow-[0_12px_30px_-18px_hsl(var(--accent)/0.9)]">
                  Service Enquiry
                </div>
                <DialogTitle className="max-w-lg text-3xl leading-tight text-primary-foreground">
                  Let's turn your next move into something magnanimous.
                </DialogTitle>
                <DialogDescription className="max-w-md text-sm leading-6 text-primary-foreground/75">
                  Share what you need and we'll come back with the right next step for your service signup.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="floating-enquire-name" className="text-primary-foreground/90">
                    Name
                  </Label>
                  <Input
                    id="floating-enquire-name"
                    name="name"
                    autoComplete="name"
                    value={form.name}
                    onChange={(event) => handleChange("name", event.target.value)}
                    placeholder="Your full name"
                    className="h-11 rounded-2xl border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_14px_35px_-24px_hsl(var(--primary)/0.95)] placeholder:text-muted-foreground/90 focus-visible:ring-2 focus-visible:ring-accent"
                  />
                  {fieldErrors.name && <p className="text-xs font-medium text-red-300">{fieldErrors.name}</p>}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="floating-enquire-email" className="text-primary-foreground/90">
                      Email
                    </Label>
                    <Input
                      id="floating-enquire-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(event) => handleChange("email", event.target.value)}
                      placeholder="you@example.com"
                      className="h-11 rounded-2xl border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_14px_35px_-24px_hsl(var(--primary)/0.95)] placeholder:text-muted-foreground/90 focus-visible:ring-2 focus-visible:ring-accent"
                    />
                    {fieldErrors.email && <p className="text-xs font-medium text-red-300">{fieldErrors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="floating-enquire-phone" className="text-primary-foreground/90">
                      Phone
                    </Label>
                    <div className="flex gap-2">
                      <Select value={countryCode} onValueChange={setCountryCode}>
                        <SelectTrigger className="h-11 w-[100px] shrink-0 rounded-2xl border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_14px_35px_-24px_hsl(var(--primary)/0.95)] focus:ring-2 focus:ring-accent">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[280px]">
                          {countryCodes.map((cc) => (
                            <SelectItem key={cc.code} value={cc.code}>
                              {cc.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="floating-enquire-phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={(event) => handlePhoneChange(event.target.value)}
                        placeholder="Your phone"
                        className="h-11 rounded-2xl border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_14px_35px_-24px_hsl(var(--primary)/0.95)] placeholder:text-muted-foreground/90 focus-visible:ring-2 focus-visible:ring-accent"
                      />
                    </div>
                    {fieldErrors.phone && <p className="text-xs font-medium text-red-300">{fieldErrors.phone}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="floating-enquire-message" className="text-primary-foreground/90">
                    What do you need help with?
                  </Label>
                  <Textarea
                    id="floating-enquire-message"
                    name="message"
                    value={form.message}
                    onChange={(event) => handleChange("message", event.target.value)}
                    placeholder="Tell us briefly about the service you want to sign up for"
                    className="min-h-[140px] rounded-[1.35rem] border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_18px_45px_-28px_hsl(var(--primary)/0.95)] placeholder:text-muted-foreground/90 focus-visible:ring-2 focus-visible:ring-accent"
                  />
                  {fieldErrors.message && <p className="text-xs font-medium text-red-300">{fieldErrors.message}</p>}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-12 w-full rounded-full border border-accent/50 bg-accent text-accent-foreground text-sm font-semibold shadow-[0_22px_40px_-20px_hsl(var(--accent)/0.95)] transition-all duration-300 hover:scale-[1.01] hover:bg-accent/95 hover:shadow-[0_28px_55px_-18px_hsl(var(--accent)/1)]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send enquiry"
                  )}
                </Button>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FloatingEnquireButton;
